/** Data-fetching hook for AI usage metrics.
 *
 * Uses react-query so data is cached across tab switches.
 * Fetches the AI usage endpoint with a shared time-range filter.
 */

import { useCallback, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { apiRequest, isApiError } from "@/shared/lib/apiClient";
import { queryKeys } from "@/shared/lib/queryKeys";
import { getTimezone } from "@/shared/lib/timezone";

import type { AIUsageResponse, MetricsRange } from "../lib/metricsTypes";
import { rangeToKey, rangeToQueryParams } from "../lib/metricsTypes";

interface UseAIMetricsResult {
  data: AIUsageResponse | null;
  loading: boolean;
  error: string | null;
  range: MetricsRange;
  setRange: (range: MetricsRange) => void;
  refetch: () => void;
}

export function useAIMetrics(initialRange: MetricsRange = "30d"): UseAIMetricsResult {
  const [range, setRange] = useState<MetricsRange>(initialRange);
  const queryClient = useQueryClient();
  const rangeKey = rangeToKey(range);
  const tz = getTimezone();
  const qp = `${rangeToQueryParams(range)}&tz=${encodeURIComponent(tz)}`;

  const { data, isLoading, error } = useQuery<AIUsageResponse>({
    queryKey: queryKeys.aiUsage(rangeKey, tz),
    queryFn: async () => {
      try {
        return await apiRequest<AIUsageResponse>("GET", `/api/metrics/ai-usage?${qp}`);
      } catch (err) {
        if (isApiError(err) && err.status === 400) {
          return {
            points: [],
            summary: { total_tokens: 0, total_input_tokens: 0, total_output_tokens: 0, total_cost_usd: 0, total_calls: 0 },
            by_feature: [],
            by_model: [],
            recent_calls: [],
          };
        }
        throw err;
      }
    },
    staleTime: 60_000,
  });

  const refetch = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["metrics", "ai-usage"] });
  }, [queryClient]);

  return {
    data: data ?? null,
    loading: isLoading,
    error: error instanceof Error ? error.message : null,
    range,
    setRange,
    refetch,
  };
}
